import { RoutesPaths } from "../../constants/routes.ts"
import { getEntityName } from "../../utils/user/get-entity-name"
import {
  canSeeLeadsArchiveContent,
  canSeeProjectsArchive
} from "../Archive/archive-permissions.ts"
import { getCustomers } from "../CustomersPage/api/services.ts"
import { getLeads } from "../Leads/api/services.ts"
import { type LeadListItem } from "../Leads/types/types.ts"
import { getProjects } from "../Projects/api/services.ts"
import { getProjectCustomerName } from "../Projects/constants/helpers.ts"
import { type ProjectListItem } from "../Projects/types/types.ts"
import type { UserPermissions } from "../SettingsPermissions/types/permissions.types.ts"

export const GLOBAL_SEARCH_MIN_LENGTH = 2
export const GLOBAL_SEARCH_PER_GROUP = 5
const ARCHIVED_PROJECTS_FIRST = 3

export type GlobalSearchGroupKey =
  | "leads"
  | "projects"
  | "customers"
  | "archive"

export interface GlobalSearchResult {
  key: string
  title: string
  subtitle: string
  href: string
  /** An archived lead or project, shown with a tag rather than hidden. */
  archived?: "lead" | "project"
}

export interface GlobalSearchGroup {
  key: GlobalSearchGroupKey
  results: GlobalSearchResult[]
  /** The group's request failed; say so instead of showing no matches. */
  failed?: boolean
}

interface Paged<T> {
  results?: T[]
}

type Response<T> = { ok: boolean; data?: Paged<T> | unknown }

const rows = <T>(response: Response<T>): T[] | null =>
  response.ok ? ((response.data as Paged<T>)?.results ?? []) : null

const joinParts = (...parts: (string | null | undefined)[]) =>
  parts.filter(Boolean).join(" · ")

const leadRow = (
  lead: LeadListItem,
  archived?: boolean,
  branch?: string
): GlobalSearchResult => {
  const customer = lead.customer || lead.temporary_customer
  const customerName = customer ? getEntityName(customer) : ""
  return {
    key: `lead:${lead.id}`,
    title: lead.lead_name || customerName || lead.uid || "",
    subtitle: joinParts(branch, lead.uid, lead.lead_name ? customerName : null),
    href: RoutesPaths.lead.view(lead.id),
    ...(archived ? { archived: "lead" as const } : {})
  }
}

const projectRow = (
  project: ProjectListItem,
  archived?: boolean,
  branch?: string
): GlobalSearchResult => ({
  key: `project:${project.id}`,
  title: project.lead_name || project.uid || "",
  subtitle: joinParts(branch, project.uid, getProjectCustomerName(project)),
  href: RoutesPaths.project.view(project.id).overview,
  ...(archived ? { archived: "project" as const } : {})
})

interface CustomerListItem {
  id: string
  first_name?: string | null
  middle_name?: string | null
  last_name?: string | null
  email?: string | null
  phone?: string | null
}

const customerRow = (customer: CustomerListItem): GlobalSearchResult => ({
  key: `customer:${customer.id}`,
  title: getEntityName(customer as never),
  subtitle: joinParts(customer.email, customer.phone),
  href: RoutesPaths.customers.view(customer.id)
})

/** The groups this user may search, in the order they are shown. */
export const searchableGroups = (
  permissions?: UserPermissions | null
): GlobalSearchGroupKey[] => {
  const pages = permissions?.pages ?? []
  const groups: GlobalSearchGroupKey[] = []
  if (pages.includes("leads")) groups.push("leads")
  if (pages.includes("projects")) groups.push("projects")
  if (pages.includes("customers")) groups.push("customers")
  if (
    canSeeProjectsArchive(permissions) ||
    canSeeLeadsArchiveContent(permissions)
  ) {
    groups.push("archive")
  }
  return groups
}

export interface SearchEverywhereOptions {
  /** Aborts every request still open when the user types on or closes. */
  signal?: AbortSignal
  /** Called as each group answers, so the box fills in as results arrive. */
  onGroup?: (group: GlobalSearchGroup) => void
  /**
   * Branch names by id, given in Company HQ. Numbers count per branch, so
   * P-000001 can be three records there and only the branch tells them apart.
   */
  branchNames?: Record<string, string>
}

/**
 * One term across the lists this user may open, through each list's own
 * search, so every result is one that list would have shown them. Groups the
 * user may not open are never asked for. The archive is asked only once the
 * other groups have mostly answered: the server serves a handful of requests
 * at a time, and five at once made every group wait.
 */
export async function searchEverywhere(
  term: string,
  permissions?: UserPermissions | null,
  { signal, onGroup, branchNames }: SearchEverywhereOptions = {}
): Promise<GlobalSearchGroup[]> {
  const branchOf = (record: { team_id?: string | null }) =>
    record.team_id ? branchNames?.[record.team_id] : undefined
  const q = term.trim()
  const page = { q, page: 1, page_size: GLOBAL_SEARCH_PER_GROUP }
  const config = { signal }
  const groups = searchableGroups(permissions)

  const searchGroup = async (
    key: GlobalSearchGroupKey
  ): Promise<GlobalSearchGroup> => {
    if (key === "leads") {
      const found = rows<LeadListItem>(await getLeads(page as never, config))
      return found
        ? {
            key,
            results: found.map((lead) => leadRow(lead, false, branchOf(lead)))
          }
        : { key, results: [], failed: true }
    }
    if (key === "projects") {
      const found = rows<ProjectListItem>(
        await getProjects(page as never, config)
      )
      return found
        ? {
            key,
            results: found.map((project) =>
              projectRow(project, false, branchOf(project))
            )
          }
        : { key, results: [], failed: true }
    }
    if (key === "customers") {
      const found = rows<CustomerListItem>(
        await getCustomers(page as never, config)
      )
      return found
        ? { key, results: found.map(customerRow) }
        : { key, results: [], failed: true }
    }

    const [projects, leads] = await Promise.all([
      canSeeProjectsArchive(permissions)
        ? getProjects({ ...page, view: "archive" } as never, config)
        : Promise.resolve({ ok: true, data: { results: [] } }),
      canSeeLeadsArchiveContent(permissions)
        ? getLeads({ ...page, view: "inactive" } as never, config)
        : Promise.resolve({ ok: true, data: { results: [] } })
    ])
    const archivedProjects = rows<ProjectListItem>(
      projects as Response<ProjectListItem>
    )
    const archivedLeads = rows<LeadListItem>(leads as Response<LeadListItem>)
    const projectRows = (archivedProjects ?? []).map((project) =>
      projectRow(project, true, branchOf(project))
    )
    const leadRows = (archivedLeads ?? []).map((lead) =>
      leadRow(lead, true, branchOf(lead))
    )
    // Share the five rows so many archived projects never hide every lead.
    const projectsShown = Math.max(
      Math.min(projectRows.length, ARCHIVED_PROJECTS_FIRST),
      GLOBAL_SEARCH_PER_GROUP - leadRows.length
    )
    const results = [...projectRows.slice(0, projectsShown), ...leadRows].slice(
      0,
      GLOBAL_SEARCH_PER_GROUP
    )
    return !archivedProjects || !archivedLeads
      ? { key, results, failed: true }
      : { key, results }
  }

  const answer = async (key: GlobalSearchGroupKey) => {
    const group = await searchGroup(key)
    if (!signal?.aborted) onGroup?.(group)
    return group
  }

  // The archive's two requests go once at most one other list is still open,
  // so no more than three are ever in flight, and the archive waits for the
  // second-slowest list, not the slowest.
  const lists = groups.filter((key) => key !== "archive")
  let open = lists.length
  let archive: Promise<GlobalSearchGroup> | undefined
  const askArchive = () => {
    if (archive || open > 1 || signal?.aborted) return
    if (groups.includes("archive")) archive = answer("archive")
  }
  askArchive()
  const found = await Promise.all(
    lists.map(async (key) => {
      const group = await answer(key)
      open -= 1
      askArchive()
      return group
    })
  )
  if (archive) found.push(await archive)
  return found
}
